/**
 * BookPageView — UX-7
 * Real page-by-page chapter reader/editor.
 *
 * - Splits chapter content into ~300-word pages (word boundary, no mid-sentence split).
 * - Shows ONE page at a time with paper aesthetic.
 * - Navigation: prev/next page, prev/next chapter, page X of Y.
 * - Paid tier: contenteditable, dashed purple focus, edited dot badge, auto-save on blur.
 * - Free tier: read-only, "👁 Read Only" label, upgrade CTA on last page only.
 * - Mobile: full-width, 48px tap zones, swipe hint, chapter context bar at top.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { ApiSuccess } from '../../lib/api';
import type { ChapterDocument } from '../../types/book';

// ─── Page splitter ────────────────────────────────────────────────────────────

const WORDS_PER_PAGE = 300;

/**
 * Split plain text into pages of ~300 words.
 * Never splits mid-sentence: extends to end of sentence if mid-split falls inside one.
 */
export function splitIntoPages(text: string, wordsPerPage = WORDS_PER_PAGE): string[] {
  if (!text.trim()) return [''];

  // Normalize line breaks
  const normalized = text.replace(/\r\n/g, '\n').trim();
  const words = normalized.split(/(\s+)/); // keep whitespace tokens

  const pages: string[] = [];
  let currentWords = 0;
  let currentText = '';
  let i = 0;

  while (i < words.length) {
    const token = words[i];
    const isWhitespace = /^\s+$/.test(token);

    if (!isWhitespace) currentWords++;
    currentText += token;
    i++;

    if (currentWords >= wordsPerPage && !isWhitespace) {
      // Try not to split mid-sentence: scan ahead for sentence end (. ! ?)
      // only if we're not at the very end
      if (i < words.length) {
        // Find next sentence boundary within the next ~50 words
        let lookahead = 0;
        let extended = currentText;
        let j = i;
        while (j < words.length && lookahead < 50) {
          const t = words[j];
          extended += t;
          j++;
          if (!/^\s+$/.test(t)) lookahead++;
          // End of sentence: last char is ., !, or ?
          if (/[.!?]$/.test(t.trimEnd()) || /[.!?]["'»\u201d]$/.test(t.trimEnd())) {
            // Found sentence boundary — commit up to here
            currentText = extended;
            i = j;
            break;
          }
        }
      }

      pages.push(currentText.trim());
      currentText = '';
      currentWords = 0;
    }
  }

  if (currentText.trim()) {
    pages.push(currentText.trim());
  }

  return pages.filter((p) => p.length > 0);
}

// ─── Component ────────────────────────────────────────────────────────────────

interface BookPageViewProps {
  chapter: ChapterDocument;
  bookId: string;
  totalChapters: number;
  isPaidUser: boolean;
  onPrevChapter?: () => void;
  onNextChapter?: () => void;
  hasPrevChapter: boolean;
  hasNextChapter: boolean;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function BookPageView({
  chapter,
  bookId,
  totalChapters,
  isPaidUser,
  onPrevChapter,
  onNextChapter,
  hasPrevChapter,
  hasNextChapter,
}: BookPageViewProps) {
  const canEdit = isPaidUser;

  // Determine display content: prefer editedContent, fall back to generatedContent/content
  const baseContent = chapter.editedContent ?? chapter.generatedContent ?? chapter.content ?? '';

  // Split into pages
  const [pages, setPages] = useState<string[]>(() => splitIntoPages(baseContent));
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [editedPages, setEditedPages] = useState<Map<number, string>>(new Map());
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [isFocused, setIsFocused] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  // Reset on chapter change
  useEffect(() => {
    const content = chapter.editedContent ?? chapter.generatedContent ?? chapter.content ?? '';
    const ps = splitIntoPages(content);
    setPages(ps);
    setCurrentPageIdx(0);
    setEditedPages(new Map());
    setSaveState('idle');
    setIsFocused(false);
  }, [chapter.chapterNumber, chapter.bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recombine all pages (using editedPages overrides) to full chapter content
  const recombineContent = useCallback(
    (updatedEditedPages: Map<number, string>) => {
      return pages
        .map((p, i) => updatedEditedPages.get(i) ?? p)
        .join('\n\n');
    },
    [pages],
  );

  const saveToServer = useCallback(
    async (fullContent: string) => {
      if (!canEdit) return;
      setSaveState('saving');
      try {
        await api.patch<ApiSuccess<{ savedAt: string }>>(
          `/books/${bookId}/chapters/${chapter.chapterNumber}`,
          { content: fullContent },
        );
        if (mounted.current) setSaveState('saved');
        setTimeout(() => {
          if (mounted.current) setSaveState('idle');
        }, 2000);
      } catch {
        if (mounted.current) setSaveState('error');
      }
    },
    [bookId, chapter.chapterNumber, canEdit],
  );

  const handlePageBlur = useCallback(() => {
    if (!canEdit || !pageRef.current) return;
    setIsFocused(false);

    const newText = pageRef.current.innerText;
    const newEditedPages = new Map(editedPages);
    newEditedPages.set(currentPageIdx, newText);
    setEditedPages(newEditedPages);

    // Auto-save: recombine all pages and send to server
    const fullContent = recombineContent(newEditedPages);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void saveToServer(fullContent);
    }, 300);
  }, [canEdit, currentPageIdx, editedPages, recombineContent, saveToServer]);

  const handlePageFocus = useCallback(() => {
    if (!canEdit) return;
    setIsFocused(true);
  }, [canEdit]);

  const currentPageText = editedPages.get(currentPageIdx) ?? pages[currentPageIdx] ?? '';
  const totalPages = pages.length;
  const isLastPage = currentPageIdx === totalPages - 1;
  const isFirstPage = currentPageIdx === 0;
  const hasEdits = editedPages.size > 0 || chapter.isEdited;

  // Navigate pages
  const goToPrevPage = () => {
    if (currentPageIdx > 0) {
      setCurrentPageIdx((p) => p - 1);
      if (pageRef.current) pageRef.current.blur();
    }
  };
  const goToNextPage = () => {
    if (currentPageIdx < totalPages - 1) {
      setCurrentPageIdx((p) => p + 1);
      if (pageRef.current) pageRef.current.blur();
    }
  };

  // Save state badge
  const saveBadge = (() => {
    if (saveState === 'saving') return '⏳ Saving…';
    if (saveState === 'saved') return '✅ Saved';
    if (saveState === 'error') return '❌ Save error';
    return null;
  })();

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Chapter context bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-neutral-800 bg-neutral-900/60 px-5 py-2.5 gap-4 flex-wrap">
        <span className="text-sm font-semibold text-white">
          Chapter {chapter.chapterNumber} — {chapter.title}
        </span>
        <div className="flex items-center gap-3">
          {!isPaidUser && (
            <span className="rounded-full bg-amber-500/15 border border-amber-500/25 px-2 py-0.5 text-[11px] font-semibold text-amber-400">
              FREE
            </span>
          )}
          <span className="text-xs text-neutral-500">
            Chapter {chapter.chapterNumber} of {totalChapters}
          </span>
          {saveBadge && (
            <span className="text-[11px] text-neutral-400">{saveBadge}</span>
          )}
        </div>
      </div>

      {/* Scrollable page area */}
      <div className="flex-1 overflow-y-auto bg-[#2a2420] px-4 sm:px-6 py-8 flex flex-col items-center">
        {/* Paper page */}
        <div
          style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          className={[
            'w-full max-w-[680px] rounded-sm relative',
            'bg-[#FFF8F0] text-[#1a1a1a]',
            'px-4 py-10 sm:px-[52px] sm:py-[44px]',
            isFocused && canEdit
              ? 'shadow-[0_2px_4px_rgba(0,0,0,0.2),0_8px_24px_rgba(0,0,0,0.45),0_20px_60px_rgba(0,0,0,0.35),inset_0_0_0_2px_rgba(124,58,237,0.4)]'
              : 'shadow-[0_2px_4px_rgba(0,0,0,0.2),0_8px_24px_rgba(0,0,0,0.45),0_20px_60px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(0,0,0,0.06)]',
          ]
            .join(' ')}
        >
          {/* Read-only label (free tier) */}
          {!isPaidUser && (
            <span
              style={{ fontFamily: 'var(--font, Inter, sans-serif)' }}
              className="absolute top-3 right-3.5 text-[10px] text-[#7c6e5b] bg-[rgba(245,240,232,0.9)] border border-[rgba(26,26,26,0.1)] rounded px-1.5 py-0.5"
            >
              👁 Read Only
            </span>
          )}

          {/* Edited dot badge (paid, when edited) */}
          {canEdit && hasEdits && (
            <span
              style={{ fontFamily: 'var(--font, Inter, sans-serif)' }}
              className="absolute top-3 left-3.5 flex items-center gap-1 text-[10px] text-[#7c6e5b]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500/60 inline-block" />
              Edited
            </span>
          )}

          {/* Edit hint (paid, not focused) */}
          {canEdit && !isFocused && !hasEdits && (
            <span
              style={{ fontFamily: 'var(--font, Inter, sans-serif)' }}
              className="absolute top-3 right-3.5 text-[10px] text-[#7c6e5b] italic"
            >
              ✏️ Click to edit
            </span>
          )}

          {/* Chapter header (first page only) */}
          {isFirstPage && (
            <div className="text-center mb-9 pb-7 border-b border-[rgba(26,26,26,0.1)]">
              <div
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                className="text-[11px] tracking-[0.18em] uppercase text-[#7c6e5b] mb-2"
              >
                Chapter {chapter.chapterNumber}
              </div>
              <div
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                className="text-[22px] font-semibold text-[#1a1a1a] leading-[1.3]"
              >
                {chapter.title}
              </div>
            </div>
          )}

          {/* Page content */}
          <div
            ref={pageRef}
            contentEditable={canEdit ? true : undefined}
            suppressContentEditableWarning
            onFocus={handlePageFocus}
            onBlur={handlePageBlur}
            aria-label={`Chapter ${chapter.chapterNumber} page ${currentPageIdx + 1}`}
            className={[
              'min-h-[320px] text-[#1a1a1a] leading-[1.85] text-[16px] sm:text-[16.5px]',
              '[&>p]:mb-[1.1em]',
              canEdit
                ? 'outline-none cursor-text'
                : 'cursor-default select-text',
              isFocused && canEdit
                ? 'border border-dashed border-violet-400/40 rounded p-2 -m-2'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            dangerouslySetInnerHTML={{
              __html: currentPageText
                .split(/\n\n+/)
                .filter(Boolean)
                .map((para, i) => {
                  const indent = i === 0 && isFirstPage ? '' : 'style="text-indent:1.6em"';
                  return `<p ${indent}>${para.trim()}</p>`;
                })
                .join(''),
            }}
          />

          {/* Free tier upgrade CTA — last page only (after user has read all free content) */}
          {!isPaidUser && isLastPage && (
            <div
              style={{ fontFamily: 'var(--font, Inter, sans-serif)' }}
              className="mt-6 bg-[rgba(26,26,26,0.05)] border border-[rgba(26,26,26,0.12)] rounded-lg p-5 text-center"
            >
              <h3 className="font-serif text-base font-semibold text-[#1a1a1a] mb-1.5">
                Unlock editing &amp; all chapters
              </h3>
              <p className="text-xs text-[#7c6e5b] mb-3 leading-relaxed">
                Free tier lets you preview Chapter 1. Upgrade to edit, continue the story, and export to KDP.
              </p>
              <Link
                to="/pricing"
                className="inline-block rounded px-4 py-2 bg-[#7c3aed] text-white text-[13px] font-semibold hover:bg-violet-700 transition-colors"
              >
                Upgrade to Starter — $19/mo →
              </Link>
            </div>
          )}
        </div>

        {/* Chapter-complete strip (last page, paid) */}
        {isLastPage && isPaidUser && (
          <div
            style={{ fontFamily: 'var(--font, Inter, sans-serif)' }}
            className="w-full max-w-[680px] mt-2.5 bg-green-500/[0.08] border border-green-500/20 rounded flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap"
          >
            <span className="text-xs text-green-400">
              ✅ End of Chapter {chapter.chapterNumber} — {chapter.title}
            </span>
            {hasNextChapter && (
              <button
                type="button"
                onClick={onNextChapter}
                className="rounded bg-[#1a1a1a] text-[#f5f0e8] text-xs font-semibold px-4 py-1.5 hover:bg-neutral-800 border-none transition-colors"
              >
                Continue to Chapter {chapter.chapterNumber + 1} →
              </button>
            )}
          </div>
        )}

        {/* Page navigation bar */}
        <div
          style={{ fontFamily: 'var(--font, Inter, sans-serif)' }}
          className="w-full max-w-[680px] mt-2.5 bg-[#1c1814] border border-white/10 rounded flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap"
        >
          {/* Prev chapter button */}
          {hasPrevChapter ? (
            <button
              type="button"
              onClick={onPrevChapter}
              className="inline-flex items-center gap-1 rounded border border-white/20 bg-white/10 text-white text-[12px] font-semibold px-3 py-1.5 hover:bg-white/20 transition-colors"
            >
              ← Chapter {chapter.chapterNumber - 1}
            </button>
          ) : (
            <span />
          )}

          {/* Page nav + indicator */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goToPrevPage}
              disabled={isFirstPage}
              className="inline-flex items-center gap-1 rounded border border-white/20 bg-white/10 text-white text-[12px] px-3 min-h-[48px] sm:min-h-[32px] py-1.5 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev Page
            </button>
            <span className="text-[12px] text-white/60 whitespace-nowrap">
              Page {currentPageIdx + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={goToNextPage}
              disabled={isLastPage}
              className="inline-flex items-center gap-1 rounded border border-white/20 bg-white/10 text-white text-[12px] px-3 min-h-[48px] sm:min-h-[32px] py-1.5 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next Page →
            </button>
          </div>

          {/* Next chapter (last page) or pages remaining */}
          {isLastPage && hasNextChapter ? (
            <button
              type="button"
              onClick={onNextChapter}
              className="inline-flex items-center gap-1 rounded bg-violet-600 text-white text-[12px] font-semibold px-3 py-1.5 hover:bg-violet-500 border-none transition-colors"
            >
              Next Chapter →
            </button>
          ) : !isLastPage ? (
            <span className="text-[11px] text-white/40">
              {totalPages - currentPageIdx - 1} page{totalPages - currentPageIdx - 1 !== 1 ? 's' : ''} remaining
            </span>
          ) : (
            <span />
          )}
        </div>

        {/* Mobile swipe hint */}
        <p
          style={{ fontFamily: 'var(--font, Inter, sans-serif)' }}
          className="mt-2 text-center text-[10px] text-neutral-600 sm:hidden"
        >
          ← swipe to navigate →
        </p>
      </div>
    </div>
  );
}
